import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell, Content } from '../../components/ui/AppShell';
import { cn } from '../../lib/cn';
import { Avatar, BrandMark } from '../../components/ui/bits';
import { SportMarkTile } from '../../components/ui/SportMarks';
import { Bell } from '../../components/ui/icons';
import { useHaptics } from '../../hooks/useHaptics';
import { useAuth } from '../../context/AuthContext';
import { upcomingCyclingLeagues } from '../../lib/cyclingSchedule';
import {
  useLeagues,
  useMyMemberships,
  useProfile,
  useUnreadByLeague
} from '../../hooks/useQueries';
import {
  FOCUSED_LEAGUE_ID,
  identityFor,
  isLeagueActive,
  LEAGUE_IDENTITIES
} from '../../data/leagues';
import { LiveCountBadge, SoonBadge } from './Badges';
import { CyclingLeagueRow } from './CyclingLeagueRow';
import { FootballLeagueRow } from './FootballLeagueRow';
import { LiveMoreSection } from './LiveRail';
import { MoreSportsTeaser } from './MoreSportsTeaser';
import { SportAccordion } from './SportAccordion';
import { StartingSoonSection } from './StartingSoon';
import { WorldCupHero } from './WorldCupHero';
import { cyclingAccordionSubtitle, FOOTBALL_IDENTITIES, formatWaitingSubtitle } from './helpers';

export default function ChooseSportScreen() {
  const navigate = useNavigate();
  const { impact } = useHaptics();
  const { uid } = useAuth();
  const { data: profile } = useProfile(uid);
  const { data: memberships } = useMyMemberships(uid);
  const { data: leagues } = useLeagues();
  const unreadByLeague = useUnreadByLeague(uid);

  const [footballOpen, setFootballOpen] = useState(false);
  const [cyclingOpen, setCyclingOpen] = useState(false);

  const name = profile?.displayName ?? 'Player';
  const firstName = name.split(' ')[0];
  const joinedLeagueIds = useMemo(
    () => new Set((memberships ?? []).map((m) => m.leagueId)),
    [memberships]
  );

  const wc = leagues?.find((l) => l.leagueId === FOCUSED_LEAGUE_ID);
  const joinedWc = joinedLeagueIds.has(FOCUSED_LEAGUE_ID);
  const allCyclingLeagues = useMemo(
    () => (leagues ?? []).filter((l) => l.sport === 'CYCLING'),
    [leagues]
  );
  // Only future/active races, ordered by start date; finished races are dropped.
  const upcomingCycling = useMemo(() => upcomingCyclingLeagues(allCyclingLeagues), [allCyclingLeagues]);
  const liveCyclingLeagues = upcomingCycling.filter((l) => l.currentRoundId);
  const unreadTotal = Object.values(unreadByLeague).reduce((sum, n) => sum + n, 0);
  const liveFootballCount = isLeagueActive(FOCUSED_LEAGUE_ID) ? 1 : 0;

  // On desktop the hero + a right-hand "live games" column sit side by side. Only
  // open that second column when it will actually hold something (a live race, or a
  // joined race counting down) — otherwise the hero spans the full width so it never
  // strands an empty rail beside it.
  const hasJoinedUpcomingCycling = upcomingCycling.some((l) => joinedLeagueIds.has(l.leagueId));
  const showLiveColumn = liveCyclingLeagues.length > 0 || hasJoinedUpcomingCycling;

  const openPlay = (leagueId: string) => {
    impact('medium');
    navigate(`/play/${leagueId}`);
  };

  const hasJoined = joinedLeagueIds.size > 0;

  return (
    <AppShell surfaceClassName="bg-white md:bg-[#FBFBFD]">
      <Content width="full">
        <div className="min-h-full bg-[#FBFBFD] pb-2">
          {/* nav bar */}
          <div className="flex items-center justify-between pt-3 md:pt-5">
            <div className="flex items-center gap-[10px]">
              <BrandMark size={34} />
              <span className="text-[16px] font-extrabold tracking-tight text-ink">HeadToHead</span>
            </div>
            <div className="flex items-center gap-[10px]">
              <button
                type="button"
                onClick={() => {
                  impact('light');
                  navigate('/notifications');
                }}
                className="tap relative grid h-[38px] w-[38px] place-items-center rounded-[12px] border border-black/[0.06] bg-[#F0F0F4]"
                aria-label="Notifications"
              >
                <Bell width={19} height={19} strokeWidth={2} className="text-[#5A5A64]" />
                {unreadTotal > 0 ? (
                  <span className="absolute -right-0.5 -top-0.5 h-[9px] w-[9px] rounded-full border-2 border-[#FBFBFD] bg-accent" />
                ) : null}
              </button>
              <button
                type="button"
                onClick={() => {
                  impact('light');
                  navigate('/profile');
                }}
                className="tap shrink-0"
                aria-label="Profile"
              >
                <Avatar
                  name={name}
                  tone={profile?.avatarTone ?? 0}
                  size={38}
                  src={profile?.photoURL}
                  className="border border-black/[0.05] shadow-[0_4px_10px_-4px_rgba(0,0,0,0.25)]"
                />
              </button>
            </div>
          </div>

          {/* greeting */}
          <div className="pt-[18px]">
            <div className="text-[12px] font-bold uppercase tracking-[1.5px] text-accent-ink">
              {hasJoined ? `Welcome back, ${firstName}` : `Welcome, ${firstName}`}
            </div>
            <h1 className="mt-[9px] text-[31px] font-extrabold leading-[1.05] tracking-title text-ink md:text-[34px] lg:text-[38px]">
              {hasJoined ? 'Where to next?' : 'Pick your first competition'}
            </h1>
            <p className="mt-[7px] max-w-xl text-[14px] font-medium leading-[1.45] text-ink2">
              {hasJoined
                ? 'Jump back into a live game — or browse every sport and league on HeadToHead.'
                : 'Join a live competition below, then build your cards or call your duels.'}
            </p>
          </div>

          {/* featured band — marquee hero + live games column (desktop) */}
          <div
            className={cn(
              'pt-5',
              showLiveColumn &&
                'md:grid md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] md:items-start md:gap-5 lg:gap-6'
            )}
          >
            {/* world cup hero */}
            <WorldCupHero
              league={wc}
              joined={joinedWc}
              uid={uid}
              membership={(memberships ?? []).find((m) => m.leagueId === FOCUSED_LEAGUE_ID)}
              onPlay={() => openPlay(FOCUSED_LEAGUE_ID)}
            />

            {/* live + starting-soon column */}
            <div>
              {/* more live rail — real live competitions besides the hero */}
              <LiveMoreSection
                leagues={liveCyclingLeagues}
                joinedLeagueIds={joinedLeagueIds}
                uid={uid}
                onOpen={openPlay}
              />

              {/* starting soon — joined cycling races not yet in play */}
              <StartingSoonSection
                joinedLeagueIds={joinedLeagueIds}
                cyclingLeagues={upcomingCycling}
                onOpen={openPlay}
              />
            </div>
          </div>

          {/* all sports */}
          <div className="flex items-baseline justify-between pb-3 pt-7">
            <span className="text-[16px] font-extrabold tracking-tight text-ink md:text-[17px] lg:text-[18px]">All sports</span>
            <button
              type="button"
              onClick={() => {
                impact('light');
                navigate('/football');
              }}
              className="tap text-[12px] font-bold text-accent-ink"
            >
              All competitions →
            </button>
          </div>

          <div className="flex flex-col gap-[11px] md:grid md:grid-cols-2 md:items-start md:gap-3">
            <SportAccordion
              open={footballOpen}
              onToggle={() => setFootballOpen((v) => !v)}
              icon={<SportMarkTile sport="football" />}
              title="Football"
              badge={
                liveFootballCount > 0 ? (
                  <LiveCountBadge count={liveFootballCount} label="LIVE" />
                ) : (
                  <SoonBadge />
                )
              }
              subtitle={`${LEAGUE_IDENTITIES.length} leagues · ${
                liveFootballCount > 0 ? 'Live now' : 'Domestic leagues between seasons'
              }`}
            >
              <FootballLeagueRow
                identity={identityFor(FOCUSED_LEAGUE_ID)!}
                league={wc}
                joined={joinedWc}
                live
                onClick={() => openPlay(FOCUSED_LEAGUE_ID)}
              />
              {FOOTBALL_IDENTITIES.map((identity) => (
                <FootballLeagueRow
                  key={identity.leagueId}
                  identity={identity}
                  league={leagues?.find((l) => l.leagueId === identity.leagueId)}
                  joined={joinedLeagueIds.has(identity.leagueId)}
                  subtitle={formatWaitingSubtitle(identity)}
                  disabled
                />
              ))}
            </SportAccordion>

            <SportAccordion
              open={cyclingOpen}
              onToggle={() => setCyclingOpen((v) => !v)}
              icon={<SportMarkTile sport="cycling" />}
              title="Cycling"
              badge={
                liveCyclingLeagues.length > 0 ? (
                  <LiveCountBadge count={liveCyclingLeagues.length} label="LIVE" />
                ) : (
                  <SoonBadge />
                )
              }
              subtitle={cyclingAccordionSubtitle(upcomingCycling, liveCyclingLeagues.length)}
            >
              {upcomingCycling.length === 0 ? (
                <p className="rounded-[16px] border border-black/[0.07] bg-white px-3 py-4 text-center text-[13px] font-medium text-ink2">
                  No upcoming cycling races — finished events are hidden automatically.
                </p>
              ) : (
                upcomingCycling.map((league) => (
                  <CyclingLeagueRow
                    key={league.leagueId}
                    league={league}
                    joined={joinedLeagueIds.has(league.leagueId)}
                    uid={uid}
                    onOpen={() => openPlay(league.leagueId)}
                  />
                ))
              )}
            </SportAccordion>

            <div className="md:col-span-2">
              <MoreSportsTeaser />
            </div>
          </div>
        </div>
      </Content>
    </AppShell>
  );
}
